/**
 * SOLID Review
 * S — Strong. One concern: the account's credits as data — the server's push, the balance read, the
 *     checkout's answer, and whose credits the client listens for.
 * O — Good. A new payment answer is one more schema.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Only the payment moves.
 * D — Strong. zod only.
 */
import { z } from 'zod';

/**
 * THE ACCOUNT'S CREDITS (frontend strict-ports plan, FE4c, 2026-10-04). A paying player's balance changes
 * in two ways the client hears of: the server charges a new game and PUSHES the payer's new balance on
 * their own topic (backend §28.190), and a Stripe purchase lands, which the payment-success screen reads
 * back. AppRoot and PaymentSuccessScreen each wrote both stores themselves; both are one decision now.
 */

/** The server's push: one player's balance after a game's charge — the wire's own field names. */
export const PayloadCreditsUpdated = z.object({ playerId: z.string(), newCredits: z.number().int() }).meta({ id: 'PayloadCreditsUpdated' });
/** Whose pushed balance to hear: the seated player, or nobody. */
export const PayloadAccountWatch = z.object({ playerId: z.string().nullable() }).meta({ id: 'PayloadAccountWatch' });
/** Stripe's hosted checkout for a credits pack. */
export const DtoCheckout = z.object({ checkoutUrl: z.string().min(1) }).meta({ id: 'DtoCheckout' });
/** The signed-in account's balance, as the server has it. */
export const DtoBalance = z.object({ matchCredits: z.number().int() }).meta({ id: 'DtoBalance' });

// ── the decisions' commands ───────────────────────────────────────────────────────────────────

export const CommandCreditsChanged = z.object({ playerId: z.string(), credits: z.number().int() }).meta({ id: 'CommandCreditsChanged' });
export const CommandBalanceRead = z.object({ credits: z.number().int() }).meta({ id: 'CommandBalanceRead' });

export type IPayloadCreditsUpdated = z.infer<typeof PayloadCreditsUpdated>;
export type IPayloadAccountWatch = z.infer<typeof PayloadAccountWatch>;
export type IEntityCheckout = z.infer<typeof DtoCheckout>;
export type IEntityBalance = z.infer<typeof DtoBalance>;
export type ICommandCreditsChanged = z.infer<typeof CommandCreditsChanged>;
export type ICommandBalanceRead = z.infer<typeof CommandBalanceRead>;
